import { SxProps, Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";

export const SIGNUP_COLORS = {
  primary: "#0055A4",
  backgroundGradient: "#054987",
  gray600: "#475467", 
  gray550: "#465869",
  gray400: "#98A2B3",
  gray200: "#E4E7EC",
  gray100: "#D0D5DD",
  white: "#FFFFFF",
  black: "#000000",
  error: "#D32F2F",
  errorRed: "#F04438",
  textPrimary: "#1D2939",
  textSecondary: "#667085",
  bodyText: "#465869",
  optionText: "#465869",
  hoverBackground: "#F9FAFB",
} as const;

export const SIGNUP_SPACING = {
  fieldGap: { xs: 2, md: 2 },
  headerGap: { xs: 3, md: 4 },
  buttonGap: { xs: 1, md: 1.5 },
} as const;

export const SIGNUP_TYPOGRAPHY = {
  fontFamily: "Manrope",
  button: {
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
  },
  label: {
    fontSize: "14px", 
    fontWeight: 500,
    lineHeight: "24px",
  },
  input: {
    fontSize: "16px",
    fontWeight: 500,
    lineHeight: "24px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: "32px",
  },
  subtitle: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
  },
} as const;

export const TYPOGRAPHY_PRESETS = {
  stepTitle: {
    fontFamily: "Manrope",
    fontWeight: 700,
    fontSize: "28px",
    lineHeight: "36px",
    color: SIGNUP_COLORS.textPrimary,
  },
  stepSubtitle: {
    fontFamily: "Manrope",
    fontWeight: 400,
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.5px",
    color: SIGNUP_COLORS.gray600,
  },
  questionTitle: {
    fontFamily: "Manrope",
    fontWeight: 600,
    fontSize: "16px",
    lineHeight: "24px",
    color: SIGNUP_COLORS.primary,
  },
  optionText: {
    fontFamily: "Manrope",
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.5px",
    fontWeight: 400,
    color: SIGNUP_COLORS.optionText,
  },
  errorText: {
    fontFamily: "Manrope",
    fontSize: "14px",
    lineHeight: "20px",
    color: SIGNUP_COLORS.error,
  },
  helperText: {
    fontFamily: "Manrope",
    fontSize: "14px",
    lineHeight: "20px",
    color: SIGNUP_COLORS.gray600,
  },
  checkboxLabel: {
    fontFamily: "Manrope",
    fontSize: "16px",
    lineHeight: "24px",
    letterSpacing: "0.2px",
    fontWeight: 400,
    color: SIGNUP_COLORS.bodyText,
  },
} as const;

export const SIGNUP_LAYOUT = {
  borderRadius: "6px",
  buttonHeight: 40,
  inputHeight: 40,
  maxWidthFirstStep: 560,
  maxWidthOtherSteps: 600,
} as const;

export const commonStyles = {
  fieldSpacing: {
    mb: SIGNUP_SPACING.fieldGap,
  } as SystemStyleObject<Theme>,

  firstRowSpacing: {
    mt: SIGNUP_SPACING.headerGap,
  } as SystemStyleObject<Theme>,

  buttonRowSpacing: {
    mt: SIGNUP_SPACING.buttonGap,
  } as SystemStyleObject<Theme>,

  primaryButton: {
    height: SIGNUP_LAYOUT.buttonHeight,
    borderRadius: SIGNUP_LAYOUT.borderRadius,
    fontSize: SIGNUP_TYPOGRAPHY.button.fontSize,
    fontWeight: SIGNUP_TYPOGRAPHY.button.fontWeight,
    textTransform: "none",
  } as SxProps,

  secondaryButton: {
    border: "1px solid #000000",
    color: SIGNUP_COLORS.gray600,
    fontWeight: SIGNUP_TYPOGRAPHY.button.fontWeight,
    fontSize: SIGNUP_TYPOGRAPHY.button.fontSize,
    borderRadius: SIGNUP_LAYOUT.borderRadius,
    textTransform: "none",
    height: SIGNUP_LAYOUT.buttonHeight,
    backgroundColor: SIGNUP_COLORS.white,
    "&:hover": {
      backgroundColor: "#F9FAFB",
      borderColor: "#000000",
    },
  } as SxProps,

  backButton: {
    border: `1px solid ${SIGNUP_COLORS.black}`,
    color: SIGNUP_COLORS.gray600,
    fontWeight: SIGNUP_TYPOGRAPHY.button.fontWeight,
    fontSize: SIGNUP_TYPOGRAPHY.button.fontSize,
    borderRadius: SIGNUP_LAYOUT.borderRadius,
    textTransform: "none",
    height: SIGNUP_LAYOUT.buttonHeight,
    backgroundColor: SIGNUP_COLORS.white,
    "&:hover": {
      backgroundColor: SIGNUP_COLORS.hoverBackground,
      borderColor: SIGNUP_COLORS.black,
    },
  } as SxProps,

  stepCard: (selected: boolean) => ({
    borderRadius: SIGNUP_LAYOUT.borderRadius,
    border: `1px solid ${selected ? SIGNUP_COLORS.primary : SIGNUP_COLORS.gray200}`,
    boxShadow: selected ? `0 0 0 3px rgba(0,85,164,0.12)` : "none",
    height: `${SIGNUP_LAYOUT.buttonHeight}px`,
    pt: "4px",
    pr: "32px", 
    pb: "4px",
    pl: "12px",
    backgroundColor: SIGNUP_COLORS.white,
    cursor: "pointer",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    display: "flex",
    alignItems: "center",
  }),

  inputLabel: {
    fontSize: SIGNUP_TYPOGRAPHY.label.fontSize,
    fontWeight: SIGNUP_TYPOGRAPHY.label.fontWeight,
    color: SIGNUP_COLORS.bodyText,
    mb: 1,
    "&.Mui-focused": { color: SIGNUP_COLORS.primary },
  } as SxProps,

  inputField: {
    borderRadius: SIGNUP_LAYOUT.borderRadius,
    "& input": {
      fontSize: SIGNUP_TYPOGRAPHY.input.fontSize,
      fontWeight: SIGNUP_TYPOGRAPHY.input.fontWeight,
      color: SIGNUP_COLORS.bodyText,
    },
    "& .MuiInputBase-root": {
      height: SIGNUP_LAYOUT.inputHeight,
    },
  } as SxProps,

  selectField: {
    borderRadius: SIGNUP_LAYOUT.borderRadius,
    "& .MuiInputLabel-root": {
      fontSize: SIGNUP_TYPOGRAPHY.label.fontSize,
      fontWeight: SIGNUP_TYPOGRAPHY.label.fontWeight,
      color: SIGNUP_COLORS.bodyText,
      mb: 1,
      "&.Mui-focused": { color: SIGNUP_COLORS.primary },
    },
    "& .MuiSelect-select": {
      fontSize: SIGNUP_TYPOGRAPHY.input.fontSize,
      fontWeight: SIGNUP_TYPOGRAPHY.input.fontWeight,
      color: SIGNUP_COLORS.bodyText,
    },
    "& .MuiOutlinedInput-root": {
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: SIGNUP_COLORS.primary,
      },
    },
  } as SxProps,
};
